import { Component } from '@angular/core'
import { AccountSessionService, CLIENT_SESSION_ENDED_MESSAGE, isSessionGoneError } from '@employer/app/services/account-session.service'
import { returnErrorNotice } from '@employer/app/services/client-switch-errors'
import { CLIENT_SWITCH_LABEL_KEY, ClientSwitchService } from '@employer/app/services/client-switch.service'
import { selectors } from '@employer/app/store/selectors'
import { IAuthService } from '@engineering11/auth-web'
import { NotificationHelper } from '@engineering11/web-ui-helpers'
import { Store } from '@ngrx/store'
import { combineLatest, map } from 'rxjs'
import { userActions } from 'shared-lib'

/**
 * Persistent "viewing a client" banner for TenantAdmin client-switch sessions (Story 1.5/1.6). Shows
 * whenever the sign-in token carries a `switchedFrom` claim (`getIsSwitchedClient`) — i.e. the session
 * was minted by the client-switch flow and can Return to its tenant. The Return control calls
 * `client-switch/return`, then re-signs-in with the tenant-scoped token via the same
 * `customTokenSignIn` → `OnLogIn` sequence the forward switch uses — never a hand-rolled sign-out.
 *
 * Distinct from `ImpersonationBannerComponent`: that banner covers cross-app platform-admin
 * impersonation (different origin, exits by closing the admin-opened tab); this one covers same-origin
 * TenantAdmin client-switch, where the only way back is re-minting the tenant token.
 *
 * Declared in `SharedModule` and mounted once, above the nav+main row, in `main-layout.component.ts`.
 */
@Component({
  selector: 'app-client-switch-banner',
  templateUrl: './client-switch-banner.component.html',
})
export class ClientSwitchBannerComponent {
  isSwitchedClient$ = this.store.select(selectors.getIsSwitchedClient)
  // The token's customerName claim is authoritative; the per-tab stash can be stale after a switch in another tab.
  clientLabel$ = combineLatest([this.store.select(selectors.getCustomerNameClaim), this.store.select(selectors.getCustomerName$)]).pipe(
    map(([claimName, nameOrKey]) => claimName || this.stashedLabel || nameOrKey)
  )

  /** Guards against a double Return while the re-sign-in round-trip is in flight. */
  returning = false

  constructor(
    private store: Store,
    private clientSwitchService: ClientSwitchService,
    private authService: IAuthService,
    private notifications: NotificationHelper,
    private accountSession: AccountSessionService
  ) {}

  get stashedLabel(): string {
    try {
      return sessionStorage.getItem(CLIENT_SWITCH_LABEL_KEY) ?? ''
    } catch {
      return ''
    }
  }

  return(): void {
    if (this.returning) return
    this.returning = true
    this.clientSwitchService.returnToTenant().subscribe({
      next: ({ token }) => this.signIn(token),
      error: err => (isSessionGoneError(err) ? this.sessionEnded() : this.fail(err)),
    })
  }

  private async signIn(customToken: string): Promise<void> {
    let signedIn = false
    try {
      this.accountSession.expectAccountChange()
      const { res, err } = await this.authService.customTokenSignIn(customToken)
      if (err || !res) {
        this.accountSession.cancelExpectedAccountChange()
        this.fail()
        return
      }
      signedIn = true
      // Force-refresh so the returned tenant session is active and persisted before we reload.
      await res.user.getIdToken(true)
      try {
        sessionStorage.removeItem(CLIENT_SWITCH_LABEL_KEY)
      } catch {}
      // Hard reload to the tenant home. The returned session is signed in + persisted, so the app
      // boots cleanly under the tenant scope — the same thing a manual refresh does. An OnLogIn-only
      // re-scope left stale banner/jobs and a stuck-disabled Return button on some returns (the
      // banner is mounted once, so its `returning` flag and the decoded-token selector lagged the
      // new session); reloading avoids that whole class of race.
      window.location.assign('/')
    } catch {
      // Firebase may already be persisted as the tenant identity; never leave that behind a "failed" toast.
      if (signedIn) this.store.dispatch(userActions.OnLogOut())
      this.fail()
    }
  }

  private sessionEnded(): void {
    this.accountSession.endSession(CLIENT_SESSION_ENDED_MESSAGE)
  }

  private fail(err?: unknown): void {
    this.returning = false
    const { type, title, message } = returnErrorNotice(err)
    this.notifications.popNotification({ type, title, message })
  }
}
